import { CompensationProfileFormInput, CompensationProfileFormProps } from "./types";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { FormStep } from "./types";
import { Controller, useForm, UseFormReturn } from "react-hook-form";
import { CompensationSummaryStep } from "./steps/CompensationSummaryStep";
import MultiStepForm from "@/components/MultiStepForm";
import BenefitDetailsStep from "./steps/BenefitDetailsStep";
import { GolfShopDetailsStep } from "./steps/GolfShopDetailsStep";
import LessonIncomeDetailsStep from "./steps/LessonIncomeDetails";
import OtherIncomeDetailsStep from "./steps/OtherIncomeDetails";
import { useMutation } from "@apollo/client";
import { UPDATE_COMPENSATION_PROFILE_MUTATION, CREATE_COMPENSATION_PROFILE_MUTATION } from "./queries";
import { Path } from "react-router-dom";
import { showSnackbar } from "@/lib/snackbarUtils";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import DateSelect from "@/components/DateSelect";
import { DateValueType } from "react-tailwindcss-datepicker";
import { DateTime } from "luxon";
import { buildHistoricalSnapshotTimestamp } from "./historicalSnapshotDate";

interface CompensationProfileFormPropsWithPgaId extends CompensationProfileFormProps {
  pgaId?: string;
  contactId?: string;
}

// Extend the form type to include our custom cleaning function
interface ExtendedUseFormReturn extends UseFormReturn<CompensationProfileFormInput> {
  cleanGolfShopData?: (formData: CompensationProfileFormInput) => CompensationProfileFormInput;
  isIncomeCategoryEnabled?: (categoryKey: string) => boolean;
}

const TIER1_FIELDS: (keyof CompensationProfileFormInput)[] = [
  "basePay",
  "basePayType",
  "lessonIncome",
  "bonusIncome",
  "otherIncome"
];

export default function CompensationProfileForm({
  isEdit = false,
  defaultValues,
  submitDialogContent = isEdit
    ? "Are you sure you want to update this compensation profile?"
    : "Are you sure you want to save compensation profile?",
  onSubmit,
  moreActions,
  errorBanner,
  setErrorBanner,
  historicalSnapshotConfig,
  pgaId,
  contactId,
  includeInitialTotalCompensation = false
}: CompensationProfileFormPropsWithPgaId) {
  const defaultHistoricalSnapshotDate = historicalSnapshotConfig
    ? DateTime.fromISO(historicalSnapshotConfig.minUpdatedAt).plus({ days: 1 }).toISODate()
    : null;

  const formDefaultValues = useCallback(
    (defaultValues?: any): CompensationProfileFormInput => {
      const {
        __typename,
        id,
        createdAt,
        updatedAt,
        isSnapshot,
        currentEmployerContainsGolfDirector,
        employerAtTimeOfRecord,
        contactLastModifiedDate,
        memberClassificationAtTimeOfRecord,
        contact,
        creationSource,
        totalCompensation,
        initialTotalCompensation,
        ...rest
      } = defaultValues || {};
      const createDefaultValues: CompensationProfileFormInput = {
        basePay: null,
        basePayType: "Salary",
        payRate: null,
        bonusIncome: null,
        lessonIncome: null,
        otherIncome: null,
        totalMonthsWorkedAnnual: null,
        employerOffers401k: false,
        employerMatches401k: false,
        employerMatch401kPercent: null,
        employerPaysPgaDues: false,
        employerOffersHealthcare: false,
        employerOffersHealthcareUsed: false,
        employerOffersDental: false,
        employerOffersVision: false,
        employerOffersLifeInsurance: false,
        employerOffersDisability: false,
        employerOffersPaidVacation: false,
        employerOffersPaidVacationWeekAmount: null,
        employerOffersEducationAllowance: false,
        employerOffersEducationAllowanceAmount: null,
        employerOffersTournamentAllowance: false,
        employerOffersTournamentAllowanceAmount: null,
        employerOffersCellphoneAllowance: false,
        employerOffersCellphoneAllowanceAmount: null,
        employerOffersCarAllowance: false,
        employerOffersCarAllowanceAmount: null,
        employerOffersClothingAllowance: false,
        employerOffersClothingAllowanceAmount: null,
        participateInGrp: false,
        otherEmploymentBenefitsDescription: "",
        golfShopOwnershipPercent: null,
        golfShopGrossSales: null,
        golfShopNetIncome: null,
        golfShopCommissionPercentage: null,
        golfShopCommissionAnnualIncome: null,
        ownsGolfShop: false,
        pgaJrlIncome: null,
        privateLessonIncome: null,
        groupLessonIncome: null,
        otherLessonIncome: null,
        lessonPercentageSplit: null,
        eventOutingCommissionAmount: null,
        eventOutingCommissionPercent: null,
        facilityCommissionAmount: null,
        facilityCommissionPercent: null,
        foodBeverageCommissionAmount: null,
        foodBeverageCommissionPercent: null,
        golfCarCommissionAmount: null,
        golfCarCommissionPercent: null,
        greenFeeCommissionAmount: null,
        greenFeeCommissionPercent: null,
        membershipSalesCommissionAmount: null,
        membershipSalesCommissionPercent: null,
        practiceRangeCommissionAmount: null,
        practiceRangeCommissionPercent: null,
        clubFittingCommissionAmount: null,
        clubFittingCommissionPercent: null,
        clubRentalCommissionAmount: null,
        clubRentalCommissionPercent: null,
        clubRepairCommissionAmount: null,
        clubRepairCommissionPercent: null,
        clubStorageCommissionAmount: null,
        clubStorageCommissionPercent: null,
        historicalSnapshotDate: defaultHistoricalSnapshotDate,
        isHistoricalSnapshot: !!historicalSnapshotConfig
      };
      if (includeInitialTotalCompensation) {
        createDefaultValues.initialTotalCompensation = null;
      }
      if (defaultValues) {
        // In edit mode, use Math.trunc on basePay to handle any legacy float data (e.g. 79500.0 -> 79500)
        // Note: Percentage values are not modified as they're already in the correct format
        const editDefaultValues: CompensationProfileFormInput = {
          ...rest,
          basePay: rest.basePay ? Math.trunc(rest.basePay) : null,
          bonusIncome: rest.bonusIncome ? rest.bonusIncome : null,
          lessonIncome: rest.lessonIncome ? rest.lessonIncome : null,
          otherIncome: rest.otherIncome ? rest.otherIncome : null,
          employerOffersPaidVacationWeekAmount: rest.employerOffersPaidVacationWeekAmount || null,
          employerOffersEducationAllowanceAmount: rest.employerOffersEducationAllowanceAmount
            ? rest.employerOffersEducationAllowanceAmount
            : null,
          employerOffersTournamentAllowanceAmount: rest.employerOffersTournamentAllowanceAmount
            ? rest.employerOffersTournamentAllowanceAmount
            : null,
          employerOffersCellphoneAllowanceAmount: rest.employerOffersCellphoneAllowanceAmount
            ? rest.employerOffersCellphoneAllowanceAmount
            : null,
          employerOffersCarAllowanceAmount: rest.employerOffersCarAllowanceAmount
            ? rest.employerOffersCarAllowanceAmount
            : null,
          employerOffersClothingAllowanceAmount: rest.employerOffersClothingAllowanceAmount
            ? rest.employerOffersClothingAllowanceAmount
            : null,
          golfShopGrossSales: rest.golfShopGrossSales ? rest.golfShopGrossSales : null,
          golfShopNetIncome: rest.golfShopNetIncome ? rest.golfShopNetIncome : null,
          golfShopCommissionAnnualIncome: rest.golfShopCommissionAnnualIncome
            ? rest.golfShopCommissionAnnualIncome
            : null,
          pgaJrlIncome: rest.pgaJrlIncome ? rest.pgaJrlIncome : null,
          privateLessonIncome: rest.privateLessonIncome ? rest.privateLessonIncome : null,
          groupLessonIncome: rest.groupLessonIncome ? rest.groupLessonIncome : null,
          otherLessonIncome: rest.otherLessonIncome ? rest.otherLessonIncome : null,
          lessonPercentageSplit: rest.lessonPercentageSplit || null,
          eventOutingCommissionAmount: rest.eventOutingCommissionAmount || null,
          facilityCommissionAmount: rest.facilityCommissionAmount || null,
          foodBeverageCommissionAmount: rest.foodBeverageCommissionAmount || null,
          golfCarCommissionAmount: rest.golfCarCommissionAmount || null,
          greenFeeCommissionAmount: rest.greenFeeCommissionAmount || null,
          membershipSalesCommissionAmount: rest.membershipSalesCommissionAmount || null,
          practiceRangeCommissionAmount: rest.practiceRangeCommissionAmount || null,
          clubRentalCommissionAmount: rest.clubRentalCommissionAmount || null,
          clubRepairCommissionAmount: rest.clubRepairCommissionAmount || null,
          clubStorageCommissionAmount: rest.clubStorageCommissionAmount || null,
          historicalSnapshotDate: defaultHistoricalSnapshotDate,
          isHistoricalSnapshot: !!historicalSnapshotConfig
        };
        if (includeInitialTotalCompensation) {
          editDefaultValues.initialTotalCompensation =
            initialTotalCompensation != null ? Math.trunc(initialTotalCompensation) : null;
        }
        return editDefaultValues;
      }
      return createDefaultValues;
    },
    [defaultHistoricalSnapshotDate, historicalSnapshotConfig, includeInitialTotalCompensation]
  );

  const backLink = {
    label: "Back",
    to: (isEdit
      ? `/people/${pgaId}/career/compensation/${defaultValues?.id}/view`
      : `/people/${pgaId}/career/compensation`) as unknown as Path,
    params: { pgaId: pgaId }
  } as any;

  const form = useForm<CompensationProfileFormInput>({
    defaultValues: formDefaultValues(defaultValues)
  }) as ExtendedUseFormReturn;

  useEffect(() => {
    if (defaultValues) {
      form.reset(formDefaultValues(defaultValues));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues]);

  const [updateCompProfile] = useMutation(UPDATE_COMPENSATION_PROFILE_MUTATION);
  const [createCompProfile] = useMutation(CREATE_COMPENSATION_PROFILE_MUTATION);
  const [submitting, setSubmitting] = useState(false);
  const [showManualOverrideDialog, setShowManualOverrideDialog] = useState(false);

  const tier1FieldValues = form.watch(TIER1_FIELDS);
  const tier1Changed =
    isEdit &&
    !!defaultValues &&
    TIER1_FIELDS.some((field, index) => {
      const currentValue = tier1FieldValues[index];
      return currentValue !== undefined && currentValue !== (defaultValues as any)[field];
    });

  const tier1Warning = (
    <>
      <span style={{ fontWeight: "bold" }}>Note:</span> Because of the types of changes that have been made, a new
      compensation profile will be created and the current selected profile will be maintained in the profile history.
    </>
  );

  const snapshotWarning = (
    <>
      <span style={{ fontWeight: "bold" }}>Warning:</span> This cannot be undone. Once historical values are overridden,
      they cannot be restored. Are you sure you want to save these updates?
    </>
  );

  let computedSubmitDialogContent: ReactNode = submitDialogContent;
  if (isEdit && tier1Changed) {
    computedSubmitDialogContent = tier1Warning;
  }
  if (isEdit && defaultValues?.isSnapshot) {
    computedSubmitDialogContent = snapshotWarning;
  }

  async function handleSubmit(formData: CompensationProfileFormInput, options?: { useUpdateOverride?: boolean }) {
    try {
      setSubmitting(true);

      // Clean the golf shop data based on the selected option
      let cleanedFormData = formData;
      if (form.cleanGolfShopData) {
        cleanedFormData = form.cleanGolfShopData(formData);
      }

      // Ensure all monetary values are submitted as whole-dollar integers
      // Note: Percentage values are not modified as they're already in the correct format
      // Don't submit initial compensatio or historical snapshot data when updating.
      const { initialTotalCompensation, historicalSnapshotDate, isHistoricalSnapshot, ...submittableCleanedFormData } =
        cleanedFormData;
      const convertedFormData = {
        ...submittableCleanedFormData,
        ...(includeInitialTotalCompensation
          ? {
              initialTotalCompensation: initialTotalCompensation != null ? Number(initialTotalCompensation) : null
            }
          : {}),
        lastModificationSource: "M360",
        basePay: cleanedFormData.basePay ? Number(cleanedFormData.basePay) : null,
        totalMonthsWorkedAnnual: cleanedFormData.totalMonthsWorkedAnnual
          ? Number(cleanedFormData.totalMonthsWorkedAnnual)
          : null,
        payRate:
          cleanedFormData.payRate && cleanedFormData.basePayType && cleanedFormData.basePayType !== "Salary"
            ? Number(cleanedFormData.payRate)
            : null,
        bonusIncome: cleanedFormData.bonusIncome ? Number(cleanedFormData.bonusIncome) : null,
        lessonIncome: cleanedFormData.lessonIncome ? Number(cleanedFormData.lessonIncome) : null,
        otherIncome: cleanedFormData.otherIncome ? Number(cleanedFormData.otherIncome) : null,
        employerOffersPaidVacationWeekAmount:
          cleanedFormData.employerOffersPaidVacation && cleanedFormData.employerOffersPaidVacationWeekAmount != null
            ? Number(cleanedFormData.employerOffersPaidVacationWeekAmount)
            : null,
        employerMatch401kPercent:
          cleanedFormData.employerOffers401k &&
          cleanedFormData.employerMatches401k &&
          cleanedFormData.employerMatch401kPercent != null
            ? Number(cleanedFormData.employerMatch401kPercent)
            : null,
        employerOffersEducationAllowanceAmount:
          cleanedFormData.employerOffersEducationAllowance && cleanedFormData.employerOffersEducationAllowanceAmount
            ? Number(cleanedFormData.employerOffersEducationAllowanceAmount)
            : null,
        employerOffersTournamentAllowanceAmount:
          cleanedFormData.employerOffersTournamentAllowance && cleanedFormData.employerOffersTournamentAllowanceAmount
            ? Number(cleanedFormData.employerOffersTournamentAllowanceAmount)
            : null,
        employerOffersCellphoneAllowanceAmount:
          cleanedFormData.employerOffersCellphoneAllowance && cleanedFormData.employerOffersCellphoneAllowanceAmount
            ? Number(cleanedFormData.employerOffersCellphoneAllowanceAmount)
            : null,
        employerOffersCarAllowanceAmount:
          cleanedFormData.employerOffersCarAllowance && cleanedFormData.employerOffersCarAllowanceAmount
            ? Number(cleanedFormData.employerOffersCarAllowanceAmount)
            : null,
        employerOffersClothingAllowanceAmount:
          cleanedFormData.employerOffersClothingAllowance && cleanedFormData.employerOffersClothingAllowanceAmount
            ? Number(cleanedFormData.employerOffersClothingAllowanceAmount)
            : null,
        golfShopGrossSales: cleanedFormData.golfShopGrossSales ? Number(cleanedFormData.golfShopGrossSales) : null,
        golfShopNetIncome: cleanedFormData.golfShopNetIncome ? Number(cleanedFormData.golfShopNetIncome) : null,
        golfShopCommissionAnnualIncome: cleanedFormData.golfShopCommissionAnnualIncome
          ? Number(cleanedFormData.golfShopCommissionAnnualIncome)
          : null,
        pgaJrlIncome: cleanedFormData.pgaJrlIncome ? Number(cleanedFormData.pgaJrlIncome) : null,
        privateLessonIncome: cleanedFormData.privateLessonIncome ? Number(cleanedFormData.privateLessonIncome) : null,
        groupLessonIncome: cleanedFormData.groupLessonIncome ? Number(cleanedFormData.groupLessonIncome) : null,
        otherLessonIncome: cleanedFormData.otherLessonIncome ? Number(cleanedFormData.otherLessonIncome) : null,
        lessonPercentageSplit: cleanedFormData.lessonPercentageSplit
          ? Number(cleanedFormData.lessonPercentageSplit)
          : null,
        eventOutingCommissionAmount:
          form.isIncomeCategoryEnabled?.("eventOuting") && cleanedFormData.eventOutingCommissionAmount
            ? Number(cleanedFormData.eventOutingCommissionAmount)
            : null,
        eventOutingCommissionPercent:
          form.isIncomeCategoryEnabled?.("eventOuting") && cleanedFormData.eventOutingCommissionPercent != null
            ? Number(cleanedFormData.eventOutingCommissionPercent)
            : null,
        facilityCommissionAmount:
          form.isIncomeCategoryEnabled?.("facility") && cleanedFormData.facilityCommissionAmount
            ? Number(cleanedFormData.facilityCommissionAmount)
            : null,
        facilityCommissionPercent:
          form.isIncomeCategoryEnabled?.("facility") && cleanedFormData.facilityCommissionPercent != null
            ? Number(cleanedFormData.facilityCommissionPercent)
            : null,
        foodBeverageCommissionAmount:
          form.isIncomeCategoryEnabled?.("foodBeverage") && cleanedFormData.foodBeverageCommissionAmount
            ? Number(cleanedFormData.foodBeverageCommissionAmount)
            : null,
        foodBeverageCommissionPercent:
          form.isIncomeCategoryEnabled?.("foodBeverage") && cleanedFormData.foodBeverageCommissionPercent != null
            ? Number(cleanedFormData.foodBeverageCommissionPercent)
            : null,
        golfCarCommissionAmount:
          form.isIncomeCategoryEnabled?.("golfCar") && cleanedFormData.golfCarCommissionAmount
            ? Number(cleanedFormData.golfCarCommissionAmount)
            : null,
        golfCarCommissionPercent:
          form.isIncomeCategoryEnabled?.("golfCar") && cleanedFormData.golfCarCommissionPercent != null
            ? Number(cleanedFormData.golfCarCommissionPercent)
            : null,
        greenFeeCommissionAmount:
          form.isIncomeCategoryEnabled?.("greenFee") && cleanedFormData.greenFeeCommissionAmount
            ? Number(cleanedFormData.greenFeeCommissionAmount)
            : null,
        greenFeeCommissionPercent:
          form.isIncomeCategoryEnabled?.("greenFee") && cleanedFormData.greenFeeCommissionPercent != null
            ? Number(cleanedFormData.greenFeeCommissionPercent)
            : null,
        membershipSalesCommissionAmount:
          form.isIncomeCategoryEnabled?.("membershipSales") && cleanedFormData.membershipSalesCommissionAmount
            ? Number(cleanedFormData.membershipSalesCommissionAmount)
            : null,
        membershipSalesCommissionPercent:
          form.isIncomeCategoryEnabled?.("membershipSales") && cleanedFormData.membershipSalesCommissionPercent != null
            ? Number(cleanedFormData.membershipSalesCommissionPercent)
            : null,
        practiceRangeCommissionAmount:
          form.isIncomeCategoryEnabled?.("practiceRange") && cleanedFormData.practiceRangeCommissionAmount
            ? Number(cleanedFormData.practiceRangeCommissionAmount)
            : null,
        practiceRangeCommissionPercent:
          form.isIncomeCategoryEnabled?.("practiceRange") && cleanedFormData.practiceRangeCommissionPercent != null
            ? Number(cleanedFormData.practiceRangeCommissionPercent)
            : null,
        clubFittingCommissionAmount:
          form.isIncomeCategoryEnabled?.("clubFitting") && cleanedFormData.clubFittingCommissionAmount
            ? Number(cleanedFormData.clubFittingCommissionAmount)
            : null,
        clubFittingCommissionPercent:
          form.isIncomeCategoryEnabled?.("clubFitting") && cleanedFormData.clubFittingCommissionPercent != null
            ? Number(cleanedFormData.clubFittingCommissionPercent)
            : null,
        clubRentalCommissionAmount:
          form.isIncomeCategoryEnabled?.("clubRental") && cleanedFormData.clubRentalCommissionAmount
            ? Number(cleanedFormData.clubRentalCommissionAmount)
            : null,
        clubRentalCommissionPercent:
          form.isIncomeCategoryEnabled?.("clubRental") && cleanedFormData.clubRentalCommissionPercent != null
            ? Number(cleanedFormData.clubRentalCommissionPercent)
            : null,
        clubRepairCommissionAmount:
          form.isIncomeCategoryEnabled?.("clubRepair") && cleanedFormData.clubRepairCommissionAmount
            ? Number(cleanedFormData.clubRepairCommissionAmount)
            : null,
        clubRepairCommissionPercent:
          form.isIncomeCategoryEnabled?.("clubRepair") && cleanedFormData.clubRepairCommissionPercent != null
            ? Number(cleanedFormData.clubRepairCommissionPercent)
            : null,
        clubStorageCommissionAmount:
          form.isIncomeCategoryEnabled?.("clubStorage") && cleanedFormData.clubStorageCommissionAmount
            ? Number(cleanedFormData.clubStorageCommissionAmount)
            : null,
        clubStorageCommissionPercent:
          form.isIncomeCategoryEnabled?.("clubStorage") && cleanedFormData.clubStorageCommissionPercent != null
            ? Number(cleanedFormData.clubStorageCommissionPercent)
            : null
      };

      let response;
      if (isEdit) {
        response = await updateCompProfile({
          variables: {
            input: {
              ...convertedFormData,
              id: defaultValues?.id,
              ...(defaultValues?.isSnapshot || options?.useUpdateOverride ? { useUpdateOverride: true } : {})
            }
          }
        });
      } else {
        const { historicalSnapshotDate } = cleanedFormData;
        const normalizedHistoricalSnapshotDate = historicalSnapshotConfig
          ? buildHistoricalSnapshotTimestamp({
              selectedDate: historicalSnapshotDate,
              minUpdatedAt: historicalSnapshotConfig.minUpdatedAt,
              maxUpdatedAt: historicalSnapshotConfig.maxUpdatedAt
            })
          : null;

        response = await createCompProfile({
          variables: {
            input: {
              ...convertedFormData,
              contactId: contactId,
              ...(historicalSnapshotConfig
                ? {
                    isHistoricalSnapshot: true,
                    historicalSnapshotDate: normalizedHistoricalSnapshotDate
                  }
                : {})
            }
          }
        });
      }
      const data = isEdit ? response.data?.updateCompensationProfile : response.data?.createCompensationProfile;
      if (data?.success) {
        showSnackbar("Compensation profile saved");
        if (onSubmit) onSubmit(data);
      } else {
        if (setErrorBanner) setErrorBanner(data?.message || "Error updating profile");
      }
    } catch (error: any) {
      if (setErrorBanner) setErrorBanner(error.message || "Error updating profile");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleShowManualOverrideDialog() {
    if (await form.trigger()) {
      setShowManualOverrideDialog(true);
    }
  }

  async function handleManualOverrideSubmit() {
    await handleSubmit(form.getValues(), { useUpdateOverride: true });
    setShowManualOverrideDialog(false);
  }

  const steps: FormStep[] = [
    {
      headline: "Compensation Summary",
      supportingText: "Step 1",
      requiredFields: historicalSnapshotConfig ? ["historicalSnapshotDate"] : [],
      renderContent: (form: UseFormReturn<CompensationProfileFormInput>) => (
        <>
          {historicalSnapshotConfig && (
            <div className="mb-6">
              <Controller
                name="historicalSnapshotDate"
                control={form.control}
                rules={{ required: "Compensation Profile Date is required" }}
                render={({ field, fieldState }) => (
                  <DateSelect
                    label="Compensation Profile Date *"
                    value={{
                      startDate: field.value || "",
                      endDate: field.value || ""
                    }}
                    onChange={(date: DateValueType | string | null) => {
                      if (typeof date === "string") return;
                      field.onChange(date?.startDate || "");
                    }}
                    supportingText="Select the approximate date for which this compensation profile would have been accurate or relevant."
                    useSingleDate
                    useMultiCalendar={false}
                    useModal
                    minDate={new Date(historicalSnapshotConfig.minUpdatedAt)}
                    maxDate={new Date(historicalSnapshotConfig.maxUpdatedAt)}
                    startFrom={new Date(historicalSnapshotConfig.minUpdatedAt)}
                    error={!!fieldState.error}
                    errorMessage={fieldState.error?.message}
                  />
                )}
              />
            </div>
          )}
          <CompensationSummaryStep form={form} includeInitialTotalCompensation={includeInitialTotalCompensation} />
        </>
      )
    },
    {
      headline: "Benefit Details",
      supportingText: "Step 2",
      requiredFields: [],
      renderContent: (form: UseFormReturn<CompensationProfileFormInput>) => <BenefitDetailsStep form={form} />
    },
    {
      headline: "Golf Shop Details",
      supportingText: "Step 3",
      requiredFields: [],
      renderContent: (form: UseFormReturn<CompensationProfileFormInput>) => <GolfShopDetailsStep form={form} />
    },
    {
      headline: "Lesson Income Details",
      supportingText: "Step 4",
      requiredFields: [],
      renderContent: (form: UseFormReturn<CompensationProfileFormInput>) => <LessonIncomeDetailsStep form={form} />
    },
    {
      headline: "Other Income Details",
      supportingText: "Step 5",
      requiredFields: [],
      renderContent: (form: UseFormReturn<CompensationProfileFormInput>) => <OtherIncomeDetailsStep form={form} />
    }
  ];

  const processedSteps = steps.map((step) => ({
    ...step,
    content: step.renderContent(form),
    requiredFields: step.requiredFields
  }));

  const shouldShowManualOverrideAction = isEdit && !defaultValues?.isSnapshot;
  const manualOverrideActions = shouldShowManualOverrideAction
    ? [
        <Button
          key="save-profile-override"
          variant="outlined"
          onClick={handleShowManualOverrideDialog}
          disabled={submitting}
        >
          Save Profile
        </Button>
      ]
    : [];
  const formActions = [...(moreActions || []), ...manualOverrideActions];
  const submitText = shouldShowManualOverrideAction ? "Update Profile" : "Save Profile";
  const submitDialogHeadline = shouldShowManualOverrideAction ? "Update Compensation Profile" : undefined;

  return (
    <>
      <MultiStepForm
        form={form}
        steps={processedSteps}
        renderStepper={true}
        onSubmit={handleSubmit}
        modelName="Compensation Profile"
        isEditing={isEdit}
        enableSubmitOnLastStep={true}
        showActions={true}
        disabledSubmit={false}
        submitText={submitText}
        customHeadline={submitDialogHeadline}
        submitDialogContent={computedSubmitDialogContent}
        isSubmitting={submitting}
        errorBanner={errorBanner}
        backLink={backLink}
        setErrorBanner={(errorBanner) => setErrorBanner?.(errorBanner || undefined)}
        moreActions={formActions}
      />
      {showManualOverrideDialog && (
        <Dialog
          open={showManualOverrideDialog}
          onClosed={() => setShowManualOverrideDialog(false)}
          headline="Save Compensation Profile"
          className="sm:max-w-[423px] p-6 pr-2"
          actions={
            <>
              <Button variant="text" onClick={() => setShowManualOverrideDialog(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button variant="filled" onClick={handleManualOverrideSubmit} disabled={submitting}>
                Save Compensation Profile
              </Button>
            </>
          }
        >
          <p>
            Saving this Compensation Profile will update its contents without creating a snapshot. Use this{" "}
            <strong>only</strong> when correcting bad data. Proceed?
          </p>
        </Dialog>
      )}
    </>
  );
}
